import { Capacitor, registerPlugin } from "@capacitor/core";
import { KYC_Status } from "entities/account";
import { sessionStore } from "entities/session/model/sessionModel";

export type FbEventExtraParams = Record<string, string>;

type FbEventParams = Record<string, string>;

interface FbEventsPlugin {
  logEvent(options: { name: string; params?: FbEventParams }): Promise<void>;
  requestTrackingAuthorization(): Promise<{ status: string }>;
}

const ATT_PROMPTED_KEY = "fb_att_prompted_after_verification";
const DEDUPE_PREFIX = "fb_ev_";
const DEPOSIT_BALANCES_KEY = "fb_deposit_balances";
const KYC_STATUS_KEY = "fb_kyc_last_status";

export const FB_EVENTS = {
  initiatedRegistration: "InitiatedRegistration",
  selectRole: "select_role",
  completeRegistration: "CompleteRegistration",
  emailVerified: "email_verified",
  kycStarted: "kyc_started",
  kycDocumentsUploaded: "kyc_documents_uploaded",
  submitApplication: "SubmitApplication",
  kycApproved: "kyc_approved",
  pinSet: "pin_set",
  taxInfoSigned: "tax_info_signed",
  contractSigned: "contract_signed",
  initiatedCheckout: "InitiatedCheckout",
  purchase: "Purchase",
  contentView: "ContentView",
  firstTrade: "first_trade",
} as const;

const FbEventsNative = registerPlugin<FbEventsPlugin>("FbEvents", {
  web: {
    logEvent: async () => {},
    requestTrackingAuthorization: async () => ({ status: "unavailable" }),
  },
});

const dedupeKey = (id: string) => `${DEDUPE_PREFIX}${id}`;

const buildParams = (extra?: FbEventExtraParams): FbEventParams | undefined => {
  const userId = sessionStore.getState().user?.id;
  const params: FbEventParams = {};

  if (userId !== undefined && userId !== null) {
    params.user_id = String(userId);
  }
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value;
      }
    }
  }

  return Object.keys(params).length ? params : undefined;
};

export const fbLogEvent = async (
  name: string,
  extra?: FbEventExtraParams,
): Promise<void> => {
  try {
    if (!Capacitor.isNativePlatform()) return;
    await FbEventsNative.logEvent({ name, params: buildParams(extra) });
  } catch (e) {
    if (localStorage.getItem("fb_events_debug") === "1") {
      console.warn("[FbEvents] logEvent failed:", name, e);
    }
  }
};

export const fbLogEventOnce = async (
  dedupeId: string,
  name: string,
  extra?: FbEventExtraParams,
): Promise<void> => {
  try {
    if (localStorage.getItem(dedupeKey(dedupeId)) === "1") return;
    await fbLogEvent(name, extra);
    localStorage.setItem(dedupeKey(dedupeId), "1");
  } catch (e) {
    if (localStorage.getItem("fb_events_debug") === "1") {
      console.warn("[FbEvents] logEventOnce failed:", name, e);
    }
  }
};

/** Deposit credited — balance increase vs last snapshot (per account). */
export const fbTrackDepositCredits = (
  accounts: { id: number; amount: number }[],
): void => {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const userId = sessionStore.getState().user?.id;
    if (!userId) return;

    const raw = localStorage.getItem(DEPOSIT_BALANCES_KEY);
    const prev: Record<string, number> = raw ? JSON.parse(raw) : {};
    const next: Record<string, number> = {};

    for (const ac of accounts) {
      const key = String(ac.id);
      next[key] = ac.amount;
      const old = prev[key];
      if (typeof old === "number" && ac.amount > old + 0.0001) {
        const dedupeId = `purchase_${userId}_${ac.id}_${Math.round(ac.amount * 100)}`;
        void fbLogEventOnce(dedupeId, FB_EVENTS.purchase);
      }
    }

    localStorage.setItem(DEPOSIT_BALANCES_KEY, JSON.stringify(next));
  } catch {
    // Analytics must not break UX.
  }
};

export const fbTrackKycStatusTransition = (
  prevStatus: KYC_Status | undefined,
  nextStatus: KYC_Status | undefined,
): void => {
  const userId = sessionStore.getState().user?.id;
  if (!userId) return;

  const statusKey = `${KYC_STATUS_KEY}_${userId}`;
  if (nextStatus !== undefined) {
    localStorage.setItem(statusKey, String(nextStatus));
  }
  if (
    nextStatus === KYC_Status.Success &&
    prevStatus !== KYC_Status.Success
  ) {
    void fbLogEventOnce(`kyc_approved_${userId}`, FB_EVENTS.kycApproved);
  }
};

/** iOS ATT — once after verification documents are submitted. */
export const requestAdvertiserTrackingAfterVerification =
  async (): Promise<void> => {
    try {
      if (!Capacitor.isNativePlatform()) return;
      if (Capacitor.getPlatform() !== "ios") return;
      if (localStorage.getItem(ATT_PROMPTED_KEY) === "true") return;

      await FbEventsNative.requestTrackingAuthorization();
      localStorage.setItem(ATT_PROMPTED_KEY, "true");
    } catch {
      // Analytics must not break UX.
    }
  };
